import type { ReactNode } from 'react';
import { useT } from '@/shared/i18n';
import type { MessageKey } from '@/shared/i18n/locales/en';
import { Icon } from '@/shared/ui/icon';
import { cn } from '@/shared/lib/cn';

export interface PasswordRequirementsProps {
  /** The new-password value being typed. */
  value: string;
  /** When provided, adds a live "passwords match" rule. */
  confirmValue?: string;
  className?: string;
}

/**
 * Live, accessible checklist for a new-password field. The length / lowercase /
 * uppercase / number rules mirror the backend password policy
 * (`shared/lib/validation.ts` → `passwordPolicy`); a match rule is added when a
 * confirm value is supplied. Met rules turn green with a check.
 */
export function PasswordRequirements({
  value,
  confirmValue,
  className,
}: PasswordRequirementsProps): ReactNode {
  const { t } = useT();

  const rules: { key: MessageKey; met: boolean }[] = [
    { key: 'password.req.length', met: value.length >= 10 },
    { key: 'password.req.lowercase', met: /[a-z]/.test(value) },
    { key: 'password.req.uppercase', met: /[A-Z]/.test(value) },
    { key: 'password.req.number', met: /\d/.test(value) },
  ];
  if (confirmValue !== undefined) {
    rules.push({
      key: 'password.req.match',
      met: value.length > 0 && value === confirmValue,
    });
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <p className="text-xs font-medium text-[var(--text-secondary)]">
        {t('password.req.title')}
      </p>
      <ul className="flex flex-col gap-1">
        {rules.map((rule) => (
          <li
            key={rule.key}
            className={cn(
              'flex items-center gap-2 text-xs',
              rule.met
                ? 'text-[var(--success-text)]'
                : 'text-[var(--text-muted)]',
            )}
          >
            {rule.met ? (
              <Icon name="check-circle" size={14} aria-hidden />
            ) : (
              <span
                aria-hidden
                className="inline-block h-3.5 w-3.5 shrink-0 rounded-full border-2 border-current opacity-50"
              />
            )}
            <span>
              {t(rule.key)}
              <span className="sr-only">
                {rule.met ? ` — ${t('password.req.met')}` : ''}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
